import React from "react";
import { Avatar, Button, Checkbox, Empty, Spin, Tag } from "antd";
import {
  CrownOutlined,
  EyeOutlined,
  MailOutlined,
  WhatsAppOutlined,
  LeftOutlined,
  RightOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import moment from "moment";

/**
 * Mobile replacement for the registrations <Table>. It deliberately reuses the table's own
 * `columns` render functions (looked up by key) so type/status/WhatsApp/check-in rendering
 * stays in one place; columns hidden for a given event kind (e.g. weddings) simply don't render.
 */
const RegistrationCardList = ({
  columns,
  data,
  loading,
  selectMode,
  selectedRowKeys,
  onSelectChange,
  onView,
  onHistory,
  pagination,
  isWedding = false,
  eventStarted = true,
  onSendWhatsApp,
  onSendEmail,
  canSend = false,
}) => {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.dir?.() === "rtl";

  const col = (key) => columns.find((c) => c.key === key);
  const renderCol = (key, value, record) => {
    const c = col(key);
    return c?.render ? c.render(value, record) : null;
  };

  const toggle = (id, checked) =>
    onSelectChange(
      checked ? [...selectedRowKeys, id] : selectedRowKeys.filter((k) => k !== id)
    );

  const { current, pageSize, total, onChange } = pagination;
  const start = total === 0 ? 0 : (current - 1) * pageSize + 1;
  const end = Math.min(current * pageSize, total);
  const dateCol = col("registrationDate");

  return (
    <Spin spinning={!!loading}>
      {data.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ padding: "24px 0" }} />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {data.map((record) => {
            const selected = selectedRowKeys.includes(record.id);
            const sendFailed = record.whatsAppDeliveryStatus === "failed" || record.whatsAppDeliveryStatus === "rejected";
            // An invitation that went out (and did not fail) is shown as Sent and cannot be sent again.
            const alreadySent = !!record.whatsAppDeliveryStatus && !sendFailed;
            return (
              <div
                key={record.id}
                // The whole card opens the details (or toggles selection while selecting);
                // the controls inside stop the tap from reaching it.
                onClick={() => (selectMode ? toggle(record.id, !selected) : onView(record))}
                style={{
                  cursor: "pointer",
                  border: `1px solid ${selected ? "#adc6ff" : "#f0f0f0"}`,
                  background: selected ? "#f0f5ff" : "#fff",
                  borderRadius: 10,
                  padding: 12,
                  boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  {selectMode && (
                    <span onClick={(e) => e.stopPropagation()} style={{ padding: 6, marginInlineStart: -6, display: "inline-flex" }}>
                      <Checkbox
                        checked={selected}
                        onChange={(e) => toggle(record.id, e.target.checked)}
                      />
                    </span>
                  )}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      flex: 1,
                      minWidth: 0,
                    }}
                  >
                    <Avatar
                      src={record.avatar}
                      icon={<UserOutlined />}
                      className={record.type === "vip" ? "border-2 border-yellow-400" : ""}
                    />
                    <div style={{ minWidth: 0 }}>
                      <div
                        style={{
                          fontWeight: 600,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {record.name}
                        {record.type === "vip" && (
                          <CrownOutlined className="text-yellow-500 ml-1" />
                        )}
                      </div>
                      <div
                        style={{
                          fontSize: 12,
                          color: "#888",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {isWedding ? (
                          record.phone && record.phone !== "N/A" ? (
                            <span dir="ltr">{record.phone}</span>
                          ) : (
                            t("eventRegistrations.card.noPhone")
                          )
                        ) : (
                          [record.position, record.company]
                            .filter((v) => v && v !== "N/A")
                            .join(" · ") || "N/A"
                        )}
                      </div>
                    </div>
                  </div>
                  <div onClick={(e) => e.stopPropagation()}>{renderCol("actions", null, record)}</div>
                </div>

                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    gap: 6,
                    marginTop: 10,
                  }}
                >
                  {renderCol("type", record.type, record)}
                  {renderCol("status", record.status, record)}
                </div>

                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 8,
                    marginTop: 10,
                    paddingTop: 10,
                    borderTop: "1px solid #f5f5f5",
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    {(eventStarted || record.checkedIn) && renderCol("checkedIn", record.checkedIn, record)}
                    {record.totalDurationInsideFormatted && (
                      <Tag color="blue" style={{ marginTop: 4 }}>
                        {record.totalDurationInsideFormatted}
                      </Tag>
                    )}
                    {dateCol && (
                      <div style={{ fontSize: 11, color: "#8c8c8c", marginTop: 4 }}>
                        {dateCol.title}: {moment(record.registrationDate).format("MMM DD, YYYY")}
                      </div>
                    )}
                  </div>
                  {eventStarted || record.checkedIn ? (
                    <Button
                      size="middle"
                      icon={<EyeOutlined />}
                      onClick={(e) => {
                        e.stopPropagation();
                        onHistory(record);
                      }}
                    >
                      {t("eventRegistrations.table.checkinHistory")}
                    </Button>
                  ) : (
                    isWedding &&
                    record.status === "pending" && (
                      // One button per channel, each with its own icon, so the icon always matches what is sent.
                      <div style={{ display: "flex", gap: 6 }}>
                        {onSendEmail && (
                          <Button
                            size="middle"
                            icon={<MailOutlined />}
                            disabled={!record.email || record.email === "N/A"}
                            title={t("eventRegistrations.actionsMenu.sendCard")}
                            aria-label={t("eventRegistrations.actionsMenu.sendCard")}
                            onClick={(e) => {
                              e.stopPropagation();
                              onSendEmail(record);
                            }}
                          />
                        )}
                        {onSendWhatsApp && (
                          <Button
                            size="middle"
                            icon={<WhatsAppOutlined style={{ color: canSend ? "#25D366" : undefined }} />}
                            disabled={alreadySent || !canSend || !record.phone || record.phone === "N/A"}
                            danger={sendFailed}
                            onClick={(e) => {
                              e.stopPropagation();
                              onSendWhatsApp(record);
                            }}
                          >
                            {alreadySent
                              ? t("eventRegistrations.card.sent")
                              : sendFailed
                                ? t("eventRegistrations.card.resend")
                                : t("eventRegistrations.card.send")}
                          </Button>
                        )}
                      </div>
                    )
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {total > 0 && (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: 12,
            paddingBottom: 72,
          }}
        >
          <span style={{ fontSize: 12, color: "#8c8c8c" }}>
            {start}–{end} / {total}
          </span>
          <div style={{ display: "flex", gap: 8 }}>
            <Button
              icon={isRtl ? <RightOutlined /> : <LeftOutlined />}
              disabled={current <= 1}
              onClick={() => onChange(current - 1, pageSize)}
            />
            <Button
              icon={isRtl ? <LeftOutlined /> : <RightOutlined />}
              disabled={end >= total}
              onClick={() => onChange(current + 1, pageSize)}
            />
          </div>
        </div>
      )}
    </Spin>
  );
};

export default RegistrationCardList;
